/**
 * Floating embers and dust in the Sconce's air (2026-10-13).
 *
 * One small canvas behind the hub's content, a fixed pool of particles (no
 * per-frame allocation: `stepParticle` mutates in place and the two sprites are
 * drawn once up front), drawn at half resolution and capped at ~30 fps - they
 * are soft points of light, nothing is lost. It does not run at all under
 * prefers-reduced-motion or Save-Data, stops while the tab is hidden, and is
 * paused by the hub while a panel covers it.
 */

import { readDepthEnv, type DepthEnv } from './hub-depth';

export interface Particle {
  x: number; y: number;
  vx: number; vy: number;
  age: number; life: number;
  size: number;
  /** 1 = ember (warm, rises, sways), 0 = dust (pale, drifts). */
  ember: number;
  phase: number;
}

export type Rand = () => number;

/** How many motes: fewer on a phone, where the fill-rate matters more. */
export function particleBudget(viewportWidth: number, coarse: boolean): number {
  const base = Math.round(viewportWidth / 48);
  const cap = coarse ? 22 : 40;
  return Math.max(12, Math.min(cap, base));
}

export function ambienceAllowed(env: DepthEnv): boolean {
  return !env.reduced && !env.saveData;
}

export function spawnParticle(p: Particle, w: number, h: number, rand: Rand, fresh: boolean): Particle {
  const ember = rand() < 0.45 ? 1 : 0;
  p.ember = ember;
  p.x = rand() * w;
  // Fresh pool: scatter through the whole screen. Respawn: from just under it.
  p.y = fresh ? rand() * h : h + 10 + rand() * 30;
  p.vy = -(ember ? 14 + rand() * 22 : 4 + rand() * 9);
  p.vx = (rand() - 0.5) * (ember ? 10 : 6);
  p.size = ember ? 5 + rand() * 7 : 3 + rand() * 4;
  p.life = 7 + rand() * 9;
  p.age = fresh ? rand() * p.life : 0;
  p.phase = rand() * Math.PI * 2;
  return p;
}

/** Advance by `dt` seconds; respawn when it has lived out its life or left the top. */
export function stepParticle(p: Particle, dt: number, w: number, h: number, rand: Rand): void {
  p.age += dt;
  p.x += (p.vx + Math.sin(p.age * 1.3 + p.phase) * (p.ember ? 9 : 3)) * dt;
  p.y += p.vy * dt;
  if (p.age >= p.life || p.y < -20 || p.x < -20 || p.x > w + 20) spawnParticle(p, w, h, rand, false);
}

/** 0..1 fade: in over the first 15 % of life, out over the last 35 %. */
export function particleAlpha(p: Particle): number {
  const t = p.age / p.life;
  if (t < 0.15) return t / 0.15;
  if (t > 0.65) return Math.max(0, (1 - t) / 0.35);
  return 1;
}

function makeSprite(inner: string, mid: string): HTMLCanvasElement | null {
  const c = document.createElement('canvas');
  c.width = c.height = 32;
  const g = c.getContext('2d');
  if (!g) return null;
  const grad = g.createRadialGradient(16, 16, 0, 16, 16, 16);
  grad.addColorStop(0, inner);
  grad.addColorStop(0.35, mid);
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 32, 32);
  return c;
}

export interface HubAmbience {
  setPaused(paused: boolean): void;
  dispose(): void;
}

const SCALE = 0.5;
const FRAME_MS = 33;

/** Mounts the canvas as a child of `host`. Returns null when it should not run. */
export function mountHubAmbience(host: HTMLElement, envIn?: DepthEnv): HubAmbience | null {
  const env = envIn ?? readDepthEnv();
  if (!ambienceAllowed(env)) return null;

  const canvas = document.createElement('canvas');
  canvas.className = 'hub-ambience';
  canvas.setAttribute('aria-hidden', 'true');
  const ctx = canvas.getContext('2d');
  const ember = makeSprite('rgba(255,214,150,1)', 'rgba(255,150,60,0.55)');
  const dust = makeSprite('rgba(255,236,205,0.9)', 'rgba(230,200,160,0.35)');
  if (!ctx || !ember || !dust) return null;
  host.appendChild(canvas);

  let w = 0; let h = 0;
  const pool: Particle[] = [];
  const rand: Rand = Math.random;

  const resize = (): void => {
    w = Math.max(1, Math.round(window.innerWidth * SCALE));
    h = Math.max(1, Math.round(window.innerHeight * SCALE));
    canvas.width = w;
    canvas.height = h;
    const want = particleBudget(window.innerWidth, env.coarse);
    while (pool.length < want) {
      pool.push(spawnParticle({ x: 0, y: 0, vx: 0, vy: 0, age: 0, life: 1, size: 1, ember: 0, phase: 0 }, w, h, rand, true));
    }
    pool.length = want;
  };
  resize();

  let paused = false;
  let raf = 0;
  let last = 0;
  let acc = 0;
  let disposed = false;

  const draw = (dt: number): void => {
    ctx.clearRect(0, 0, w, h);
    for (let i = 0; i < pool.length; i++) {
      const p = pool[i];
      stepParticle(p, dt, w, h, rand);
      const a = particleAlpha(p) * (p.ember ? 0.85 : 0.5);
      if (a <= 0.01) continue;
      ctx.globalAlpha = a;
      ctx.drawImage(p.ember ? ember : dust, p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
    }
    ctx.globalAlpha = 1;
  };

  const loop = (now: number): void => {
    raf = 0;
    if (disposed || paused || document.hidden) return;
    const dtMs = last ? Math.min(now - last, 100) : FRAME_MS;
    last = now;
    acc += dtMs;
    if (acc >= FRAME_MS) { draw(acc / 1000); acc = 0; }
    raf = requestAnimationFrame(loop);
  };

  const start = (): void => {
    if (raf || disposed || paused || document.hidden) return;
    last = 0;
    raf = requestAnimationFrame(loop);
  };
  const stop = (): void => {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
  };

  const onVisibility = (): void => { if (document.hidden) stop(); else start(); };
  const onResize = (): void => { resize(); };
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('resize', onResize, { passive: true });
  start();

  return {
    setPaused(p: boolean): void {
      paused = p;
      if (p) { stop(); ctx.clearRect(0, 0, w, h); } else start();
    },
    dispose(): void {
      disposed = true;
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('resize', onResize);
      canvas.remove();
    },
  };
}
